import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { Controller, FieldValues, Path } from "react-hook-form";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useGetFilters } from "@src/entities/requestHH/model/hooks/useGetFilters/useGetFilters";
import crossIcon from "@src/shared/assets/cross_icon.svg";
import openIcon from "@src/shared/assets/open_icon.svg";
import CheckIcon from "@mui/icons-material/Check";
import { DriverLicenseField } from "../DriverLicenseCategories/DriverLicenseCategories";
import { ExclusiveCheckboxGroup } from "../ExclusiveCheckboxGroup/ExclusiveCheckboxGroup";

import styles from "./AdditionalParamsField.module.scss";

type Props<T extends FieldValues> = {
  control: any;
  citizenshipName: Path<T>;
  nameProfEducation: Path<T>;
  nameFrequentChange: Path<T>;
  isHasCar: Path<T>;
  educationName: Path<T>;
  isFrequentJobChanges: Path<T>;
  jobStatusName: Path<T>;
  isWithoutAge: Path<T>;
  isDisabledFields: boolean;
};

const crossImg = <img src={crossIcon} />;
const openImg = <img src={openIcon} className={styles.openImg} />;

export const AdditionalParamsField = <T extends FieldValues>({
  control,
  citizenshipName,
  isHasCar,
  isFrequentJobChanges,
  isDisabledFields,
  jobStatusName,
}: Props<T>) => {
  const [expanded, setExpanded] = useState(true);
  const { data: filters } = useGetFilters();

  const handleToggle = () => setExpanded((prev) => !prev);

  const { job_search_status, driver_license_types } = filters?.data || {};
  // const CITIZENSHIP_OPTIONS = citizenship || [];
  // const EDUCATION_OPTIONS = education_levels || [];
  // const EXPERIENCE_OPTIONS = experience_levels || [];
  const JOB_STATUS_OPTIONS = job_search_status || [];
  const DRIVER_LICENSE_OPTIONS =
    driver_license_types?.map((el) => ({ ...el, name: el.id })) || [];

  return (
    <Accordion
      expanded={expanded}
      sx={{
        mt: 0,
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        borderRadius: "25px",
        padding: "17px 30px",
        color: "rgba(38, 38, 38, 1)",
        marginTop: "10px",
        marginBottom: "15px",
        boxShadow: "none",
        "&:before": {
          display: "none",
        },
        "&.MuiAccordion-root": { borderRadius: "25px" },
      }}
    >
      <AccordionSummary
        sx={{
          backgroundColor: "inherit",
          height: "30px",
          px: 0,
          "& .MuiAccordionSummary-content": {
            margin: 0,
          },
        }}
      >
        <Box
          onClick={handleToggle}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            cursor: "pointer",
            width: "100%",
            height: "30px",
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          <h3>Дополнительно</h3>
          <div className={styles.iconWrapper}>
            {expanded ? (
              <ExpandLess sx={{ color: "rgba(164, 187, 199, 1)" }} />
            ) : (
              <ExpandMore sx={{ color: "rgba(164, 187, 199, 1)" }} />
            )}
          </div>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 0,
          backgroundColor: "inherit",
          py: 0,
          mt: 0,
          mb: 0,
          gap: 0,
          borderTop: "1px solid rgba(164, 187, 199, 0.25)",
          paddingTop: "20px",
        }}
      >
        <Controller
          name={jobStatusName}
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <div className={styles.educationTitle}>
                <span>Статус резюме:</span>
              </div>
              <Autocomplete
                multiple
                options={JOB_STATUS_OPTIONS}
                disabled={isDisabledFields}
                className={styles.autocomplete}
                disableCloseOnSelect
                clearIcon={crossImg}
                popupIcon={openImg}
                getOptionLabel={(option) => {
                  return typeof option === "object" ? option.name : option;
                }}
                isOptionEqualToValue={(option, val) =>
                  option.hh_id === val.hh_id
                }
                value={value}
                onChange={(_, newValue) => onChange(newValue)}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={`${option.name}_${option.hh_id}`}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                      key={option.hh_id}
                      disableRipple
                    />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={"Укажите cтатус резюме"}
                    variant="outlined"
                    className={styles.inputField}
                    sx={{
                      borderRadius: "40px",
                      "& input": {
                        color: "rgba(38, 38, 38, 1)",

                        "&:-webkit-autofill": {
                          WebkitTextFillColor: "#fff",
                          caretColor: "rgba(38, 38, 38, 1)",
                          transition: "background-color 5000s ease-in-out 0s",
                        },
                      },
                      "& .MuiOutlinedInput-root": {
                        minHeight: 40,
                        alignItems: "center",
                        alignContent: "center",
                        display: "flex",
                        "& fieldset": {
                          borderColor: "rgba(164, 187, 199, 1)",
                          background: "rgba(164, 187, 199, 0.25)",
                        },
                        "&.Mui-disabled:hover fieldset": {
                          borderColor: "currentcolor",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(164, 187, 199, 1)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "rgba(164, 187, 199, 1)",
                        },
                        "& .MuiOutlinedInput-input.Mui-disabled": {
                          WebkitTextFillColor: "rgba(255, 255, 255, 0.7)",
                        },
                      },
                      ".MuiFormHelperText-root.Mui-error": {
                        color: "rgba(255, 255, 255, 0.8)",
                      },
                      ".MuiFormHelperText-root.Mui-disabled": {
                        color: "rgba(255, 255, 255, 0.8)",
                        fontWeight: 400,
                      },
                    }}
                  />
                )}
                sx={{
                  mt: 2,
                  ".MuiAutocomplete-popupIndicator": { color: "#fff" },
                  ".MuiSvgIcon-root": { color: "#fff" },
                  color: "#fff",
                }}
              />
            </>
          )}
        />
        <Controller
          name={citizenshipName}
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  icon={<span className={styles.customBox} />}
                  disabled={isDisabledFields}
                  checkedIcon={
                    <span className={`${styles.customBox} ${styles.checked}`}>
                      <CheckIcon />
                    </span>
                  }
                />
              }
              label="Гражданство РФ"
              className={styles.citizenshipLabel}
            />
          )}
        />
        {/* Ждем бэк*/}
        <div className={styles.ckeckboxBlock}>
          <div className={styles.jobChanges}>
            <Controller
              name={isFrequentJobChanges}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      disabled={isDisabledFields}
                      checked={field.value}
                      icon={<span className={styles.customBox} />}
                      checkedIcon={
                        <span
                          className={`${styles.customBox} ${styles.checked}`}
                        >
                          <CheckIcon />
                        </span>
                      }
                    />
                  }
                  label="Исключить кандидатов с частой сменой работы"
                  className={styles.salaryLabel}
                />
              )}
            />
          </div>
        </div>
        <div className={styles.carField}>
          <span className={styles.label}>Наличие личного автомобиля:</span>
          <ExclusiveCheckboxGroup
            disabled={isDisabledFields}
            control={control}
            name={isHasCar}
            options={[
              {
                name: "Да",
                id: true,
              },
              {
                name: "Нет",
                id: false,
              },
            ]}
          />
        </div>
        <DriverLicenseField
          control={control}
          name="driverLicenseCategories"
          options={DRIVER_LICENSE_OPTIONS}
          isDisabled={isDisabledFields}
        />
      </AccordionDetails>
    </Accordion>
  );
};
